import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { AdminFeedbackService } from "@/services/feedback/adminFeedbackService";
import type { BugReportStatus } from "@/services/feedback/adminFeedbackService";

export const ADMIN_FEEDBACK_LIST_KEY = "admin-feedback";
export const ADMIN_BUG_REPORTS_LIST_KEY = "admin-bug-reports";
export const ADMIN_FEEDBACK_UNREAD_KEY = "admin-feedback-unread";
export const ADMIN_FEEDBACK_DETAIL_KEY = "admin-feedback-detail";
export const ADMIN_BUG_REPORT_DETAIL_KEY = "admin-bug-report-detail";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useAdminFeedback(enabled: boolean) {
  const queryClient = useQueryClient();

  const feedbackHasUnreadQuery = useQuery({
    queryKey: [ADMIN_FEEDBACK_UNREAD_KEY, "feedback"],
    queryFn: () => AdminFeedbackService.getFeedbackHasUnread(),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const bugReportHasUnreadQuery = useQuery({
    queryKey: [ADMIN_FEEDBACK_UNREAD_KEY, "bug-reports"],
    queryFn: () => AdminFeedbackService.getBugReportHasUnread(),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const hasUnread =
    (feedbackHasUnreadQuery.data ?? false) || (bugReportHasUnreadQuery.data ?? false);

  return {
    hasUnread,
    isLoadingHasUnread:
      feedbackHasUnreadQuery.isLoading || bugReportHasUnreadQuery.isLoading,
    refetchHasUnread: () => {
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_UNREAD_KEY] });
    },
  };
}

export function useAdminFeedbackList(
  feedbackPage: number,
  feedbackLimit: number,
  bugPage: number,
  bugLimit: number,
  enabledFeedback: boolean,
  enabledBugs: boolean,
  bugStatusFilter: BugReportStatus | "ALL" | null = null
) {
  const queryClient = useQueryClient();

  const feedbackListQuery = useQuery({
    queryKey: [ADMIN_FEEDBACK_LIST_KEY, feedbackPage, feedbackLimit],
    queryFn: () => AdminFeedbackService.getFeedbackList(feedbackPage, feedbackLimit),
    enabled: enabledFeedback,
    staleTime: 30 * 1000,
  });

  const bugReportListQuery = useQuery({
    queryKey: [ADMIN_BUG_REPORTS_LIST_KEY, bugPage, bugLimit, bugStatusFilter],
    queryFn: () =>
      AdminFeedbackService.getBugReportList(
        bugPage,
        bugLimit,
        bugStatusFilter && bugStatusFilter !== "ALL" ? bugStatusFilter : undefined
      ),
    enabled: enabledBugs,
    staleTime: 30 * 1000,
  });

  return {
    feedbackList: feedbackListQuery.data,
    isLoadingFeedbackList: feedbackListQuery.isLoading,
    feedbackListError: feedbackListQuery.error,
    refetchFeedbackList: feedbackListQuery.refetch,

    bugReportList: bugReportListQuery.data,
    isLoadingBugReportList: bugReportListQuery.isLoading,
    bugReportListError: bugReportListQuery.error,
    refetchBugReportList: bugReportListQuery.refetch,

    invalidateLists: () => {
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADMIN_BUG_REPORTS_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_UNREAD_KEY] });
    },
  };
}

export function useAdminFeedbackDetail(feedbackId: string | null, enabled: boolean) {
  const queryClient = useQueryClient();
  const invalidatedForRef = useRef<string | null>(null);

  const detailQuery = useQuery({
    queryKey: [ADMIN_FEEDBACK_DETAIL_KEY, feedbackId],
    queryFn: () => AdminFeedbackService.getFeedbackById(feedbackId!),
    enabled: enabled && !!feedbackId,
    staleTime: 0,
  });

  useEffect(() => {
    if (detailQuery.data && feedbackId && invalidatedForRef.current !== feedbackId) {
      invalidatedForRef.current = feedbackId;
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_UNREAD_KEY] });
    }
  }, [detailQuery.data, feedbackId, queryClient]);

  return {
    feedbackDetail: detailQuery.data ?? null,
    isLoadingFeedbackDetail: detailQuery.isLoading,
    feedbackDetailError: detailQuery.error,
    refetchFeedbackDetail: detailQuery.refetch,
    clearFeedbackDetail: () => {
      queryClient.removeQueries({ queryKey: [ADMIN_FEEDBACK_DETAIL_KEY, feedbackId] });
    },
  };
}

export function useAdminBugReportDetail(bugReportId: string | null, enabled: boolean) {
  const queryClient = useQueryClient();
  const invalidatedForRef = useRef<string | null>(null);

  const detailQuery = useQuery({
    queryKey: [ADMIN_BUG_REPORT_DETAIL_KEY, bugReportId],
    queryFn: () => AdminFeedbackService.getBugReportById(bugReportId!),
    enabled: enabled && !!bugReportId,
    staleTime: 0,
  });

  useEffect(() => {
    if (detailQuery.data && bugReportId && invalidatedForRef.current !== bugReportId) {
      invalidatedForRef.current = bugReportId;
      queryClient.invalidateQueries({ queryKey: [ADMIN_BUG_REPORTS_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_UNREAD_KEY] });
    }
  }, [detailQuery.data, bugReportId, queryClient]);

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: BugReportStatus }) =>
      AdminFeedbackService.updateBugReportStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_BUG_REPORT_DETAIL_KEY, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [ADMIN_BUG_REPORTS_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_UNREAD_KEY] });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: ({ id, text, mentions }: { id: string; text: string; mentions?: string[] }) =>
      AdminFeedbackService.addBugReportComment(id, text, mentions),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [ADMIN_BUG_REPORT_DETAIL_KEY, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [ADMIN_BUG_REPORTS_LIST_KEY] });
      queryClient.invalidateQueries({ queryKey: [ADMIN_FEEDBACK_UNREAD_KEY] });
    },
  });

  return {
    bugReportDetail: detailQuery.data ?? null,
    isLoadingBugReportDetail: detailQuery.isLoading,
    bugReportDetailError: detailQuery.error,
    refetchBugReportDetail: detailQuery.refetch,
    clearBugReportDetail: () => {
      queryClient.removeQueries({
        queryKey: [ADMIN_BUG_REPORT_DETAIL_KEY, bugReportId],
      });
    },
    updateStatus: updateStatusMutation.mutateAsync,
    isUpdatingStatus: updateStatusMutation.isPending,
    addComment: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
  };
}
